import React, { Component } from 'react';
import IconButton from 'material-ui/IconButton';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
import IconMenu from 'material-ui/IconMenu';
import Bold from 'material-ui/svg-icons/editor/format-bold';
import Italic from 'material-ui/svg-icons/editor/format-italic';
import UnderlineIcon from 'material-ui/svg-icons/editor/format-underlined';
import UL from 'material-ui/svg-icons/editor/format-list-bulleted';
import OL from 'material-ui/svg-icons/editor/format-list-numbered';
import Left from 'material-ui/svg-icons/editor/format-align-left';
import Right from 'material-ui/svg-icons/editor/format-align-right';
import Centered from 'material-ui/svg-icons/editor/format-align-center';
import Justify from 'material-ui/svg-icons/editor/format-align-justify';
import Quote from 'material-ui/svg-icons/editor/format-quote';
import Undo from 'material-ui/svg-icons/content/undo';
import Redo from 'material-ui/svg-icons/content/redo';
import ClearFormatting from 'material-ui/svg-icons/content/clear';
import Table from 'material-ui/svg-icons/action/view-module';
import EditorAttachMoney from 'material-ui/svg-icons/editor/attach-money';
import InsertLink from 'material-ui/svg-icons/editor/insert-link';
import LinkOff from '@mui/icons-material/LinkOff';
import { insertVariable } from './VariableExtension';
import LinkDialog from './LinkDialog';
import { normalizeUrl, selectionText } from './linkUtils';

const HEADINGS = [
  { value: 0, label: 'Normální' },
  { value: 1, label: 'Nadpis 1', element: 'h1' },
  { value: 2, label: 'Nadpis 2', element: 'h2' },
  { value: 3, label: 'Nadpis 3', element: 'h3' },
  { value: 4, label: 'Nadpis 4', element: 'h4' },
  { value: 5, label: 'Nadpis 5', element: 'h5' },
  { value: 6, label: 'Nadpis 6', element: 'h6' },
];

const ALIGN_BUTTONS = [
  { label: 'Zarovnat vlevo', align: 'left', Icon: Left },
  { label: 'Zarovnat na střed', align: 'center', Icon: Centered },
  { label: 'Zarovnat vpravo', align: 'right', Icon: Right },
  { label: 'Zarovnat do bloku', align: 'justify', Icon: Justify },
];

const ACTIVE_COLOR = '#1f5f9d';
const INACTIVE_COLOR = 'black';

class Toolbar extends Component {
  state = {
    linkDialogOpen: false,
    linkUrl: '',
    linkText: '',
    linkExisting: false,
  };

  componentDidMount() {
    const { editor } = this.props;
    if (editor) {
      editor.on('selectionUpdate', this.handleEditorUpdate);
      editor.on('transaction', this.handleEditorUpdate);
    }
  }

  componentDidUpdate(prevProps) {
    const { editor } = this.props;
    if (prevProps.editor !== editor) {
      if (prevProps.editor) {
        prevProps.editor.off('selectionUpdate', this.handleEditorUpdate);
        prevProps.editor.off('transaction', this.handleEditorUpdate);
      }
      if (editor) {
        editor.on('selectionUpdate', this.handleEditorUpdate);
        editor.on('transaction', this.handleEditorUpdate);
      }
    }
  }

  componentWillUnmount() {
    const { editor } = this.props;
    if (editor) {
      editor.off('selectionUpdate', this.handleEditorUpdate);
      editor.off('transaction', this.handleEditorUpdate);
    }
  }

  handleEditorUpdate = () => {
    this.forceUpdate();
  };

  preventDefault = (e) => {
    e.preventDefault();
  };

  handleUndo = (e) => {
    e.preventDefault();
    this.props.editor.chain().focus().undo().run();
  };

  handleRedo = (e) => {
    e.preventDefault();
    this.props.editor.chain().focus().redo().run();
  };

  handleBold = (e) => {
    e.preventDefault();
    this.props.editor.chain().focus().toggleBold().run();
  };

  handleItalic = (e) => {
    e.preventDefault();
    this.props.editor.chain().focus().toggleItalic().run();
  };

  handleUnderline = (e) => {
    e.preventDefault();
    this.props.editor.chain().focus().toggleUnderline().run();
  };

  handleClearFormatting = (e) => {
    e.preventDefault();
    this.props.editor.chain().focus().clearNodes().unsetAllMarks().run();
  };

  handleHeadingChange = (event, index, value) => {
    if (value === 0) {
      this.props.editor.chain().focus().setParagraph().run();
    } else {
      this.props.editor.chain().focus().setHeading({ level: value }).run();
    }
  };

  handleBulletList = (e) => {
    e.preventDefault();
    this.props.editor.chain().focus().toggleBulletList().run();
  };

  handleOrderedList = (e) => {
    e.preventDefault();
    this.props.editor.chain().focus().toggleOrderedList().run();
  };

  handleBlockquote = (e) => {
    e.preventDefault();
    this.props.editor.chain().focus().toggleBlockquote().run();
  };

  handleAlign = (align) => (e) => {
    e.preventDefault();
    this.props.editor.chain().focus().setTextAlign(align).run();
  };

  handleInsertTable = (e) => {
    e.preventDefault();
    this.props.editor
      .chain()
      .focus()
      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
      .run();
  };

  handleInsertVariable = (id) => () => {
    insertVariable(this.props.editor, id);
  };

  handleOpenLinkDialog = (e) => {
    e.preventDefault();
    const { editor } = this.props;
    // Grow the selection to cover the whole link when the caret merely sits
    // inside one, so editing works without selecting the text by hand.
    editor.chain().focus().extendMarkRange('link').run();
    this.setState({
      linkDialogOpen: true,
      linkUrl: editor.getAttributes('link').href || '',
      linkText: selectionText(editor),
      linkExisting: editor.isActive('link'),
    });
  };

  handleCloseLinkDialog = () => {
    this.setState({ linkDialogOpen: false });
    const { editor } = this.props;
    if (editor) editor.commands.focus();
  };

  handleSubmitLink = ({ url, text }) => {
    const { editor } = this.props;
    const href = normalizeUrl(url);
    if (!href) return;

    const current = selectionText(editor);
    const label = text || href;
    const chain = editor.chain().focus().extendMarkRange('link');

    if (editor.state.selection.empty || label !== current) {
      // No selection, or the user rewrote the label — write plain linked text.
      chain
        .insertContent({ type: 'text', text: label, marks: [{ type: 'link', attrs: { href } }] })
        .unsetMark('link')
        .run();
    } else {
      // Label untouched — only stamp the mark, keeping bold/italic/variables.
      chain.setLink({ href }).run();
    }

    this.setState({ linkDialogOpen: false });
  };

  handleRemoveLink = () => {
    this.props.editor.chain().focus().extendMarkRange('link').unsetLink().run();
    this.setState({ linkDialogOpen: false });
  };

  handleUnsetLink = (e) => {
    e.preventDefault();
    this.props.editor.chain().focus().extendMarkRange('link').unsetLink().run();
  };

  getCurrentHeading = () => {
    const { editor } = this.props;
    if (!editor) return 0;
    for (let level = 1; level <= 6; level += 1) {
      if (editor.isActive('heading', { level })) return level;
    }
    return 0;
  };

  render() {
    const { editor, variables } = this.props;
    if (!editor) return <div className="cro-tiptap-toolbar" />;

    const currentHeading = this.getCurrentHeading();
    const isBold = editor.isActive('bold');
    const isItalic = editor.isActive('italic');
    const isUnderline = editor.isActive('underline');
    const isBullet = editor.isActive('bulletList');
    const isOrdered = editor.isActive('orderedList');
    const isQuote = editor.isActive('blockquote');
    const isLink = editor.isActive('link');
    const { linkDialogOpen, linkUrl, linkText, linkExisting } = this.state;

    return (
      <React.Fragment>
        <div className="cro-tiptap-toolbar" onMouseDown={this.preventDefault}>
          <div className="toolbar-row">
            <IconButton tooltip="Zpět" onMouseDown={this.handleUndo}>
              <Undo />
            </IconButton>
            <IconButton tooltip="Vpřed" onMouseDown={this.handleRedo}>
              <Redo />
            </IconButton>
            <span className="toolbar-sep" />
            <SelectField
              className="toolbar-select"
              value={currentHeading}
              onChange={this.handleHeadingChange}
              style={{ width: 250, height: 40, paddingLeft: 5 }}
            >
              {HEADINGS.map(h => {
                    const Tag = h.element || 'span';
                    return <MenuItem key={h.label} value={h.value} primaryText={<Tag>{h.label}</Tag>}/>;
                  }
              )}
            </SelectField>
            <span className="toolbar-sep" />
            <IconButton tooltip="Tučně" onMouseDown={this.handleBold}>
              <Bold color={isBold ? ACTIVE_COLOR : INACTIVE_COLOR} />
            </IconButton>
            <IconButton tooltip="Italika" onMouseDown={this.handleItalic}>
              <Italic color={isItalic ? ACTIVE_COLOR : INACTIVE_COLOR} />
            </IconButton>
            <IconButton tooltip="Podtrhnout" onMouseDown={this.handleUnderline}>
              <UnderlineIcon color={isUnderline ? ACTIVE_COLOR : INACTIVE_COLOR} />
            </IconButton>
            <IconButton tooltip="Vymazat formátování" onMouseDown={this.handleClearFormatting}>
              <ClearFormatting />
            </IconButton>
            <span className="toolbar-sep" />
            <IconButton tooltip="Nečíslovaný seznam" onMouseDown={this.handleBulletList}>
              <UL color={isBullet ? ACTIVE_COLOR : INACTIVE_COLOR} />
            </IconButton>
            <IconButton tooltip="Číslovaný seznam" onMouseDown={this.handleOrderedList}>
              <OL color={isOrdered ? ACTIVE_COLOR : INACTIVE_COLOR} />
            </IconButton>
            <IconButton tooltip="Citace" onMouseDown={this.handleBlockquote}>
              <Quote color={isQuote ? ACTIVE_COLOR : INACTIVE_COLOR} />
            </IconButton>
            <span className="toolbar-sep" />
            {ALIGN_BUTTONS.map((btn) => {
              const isActive = editor.isActive({ textAlign: btn.align });
              return (
                <IconButton key={btn.align} tooltip={btn.label} onMouseDown={this.handleAlign(btn.align)}>
                  <btn.Icon color={isActive ? ACTIVE_COLOR : INACTIVE_COLOR} />
                </IconButton>
              );
            })}
            <span className="toolbar-sep" />
            <IconButton
              tooltip={isLink ? 'Upravit odkaz' : 'Vložit odkaz'}
              onMouseDown={this.handleOpenLinkDialog}
            >
              <InsertLink color={isLink ? ACTIVE_COLOR : INACTIVE_COLOR} />
            </IconButton>
            <IconButton
              tooltip="Odstranit odkaz"
              disabled={!isLink}
              onMouseDown={this.handleUnsetLink}
            >
              <LinkOff htmlColor={isLink ? INACTIVE_COLOR : '#bbb'} />
            </IconButton>
            <span className="toolbar-sep" />
            <IconButton tooltip="Vložit tabulku" onMouseDown={this.handleInsertTable}>
              <Table />
            </IconButton>
            {variables && variables.length > 0 && (
              <IconMenu
                iconButtonElement={<IconButton tooltip="Proměnné"><EditorAttachMoney /></IconButton>}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                targetOrigin={{ horizontal: 'left', vertical: 'top' }}
              >
                {variables.map((v) => (
                  <MenuItem
                    key={v.id}
                    primaryText={v.label || v.id}
                    onClick={this.handleInsertVariable(v.id)}
                  />
                ))}
              </IconMenu>
            )}
          </div>
        </div>
        <LinkDialog
          open={linkDialogOpen}
          initialUrl={linkUrl}
          initialText={linkText}
          canRemove={linkExisting}
          onSubmit={this.handleSubmitLink}
          onRemove={this.handleRemoveLink}
          onClose={this.handleCloseLinkDialog}
        />
      </React.Fragment>
    );
  }
}

export default Toolbar;
